import { useEffect, useState } from "react";
import {
  Autocomplete,
  DatePicker,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
} from "../../../../components";
import { value } from "../../../../helpers";
import {
  BancosCaixasService,
  CentroCustoService,
  MeioPagamentoService,
  PessoaService,
  PlanosFinanceirosService,
  SessionService,
} from "../../../../services";
import { responseErros } from "../../../../utils";
import { useFiltrosRelatoriosHandlers } from "../../components/use-filtros-relatorios-handlers";
import {
  listaAgrupadoresContasPagar,
  listaStatus,
  listaTiposContasPagar,
} from "./entities/constantes";

const pessoaService = new PessoaService();
const bancosCaixasService = new BancosCaixasService();
const meioPagamentoService = new MeioPagamentoService();
const planoFinanceiroService = new PlanosFinanceirosService();
const centroCustoService = new CentroCustoService();

const FormFiltroAvancado = ({
  filtrosAvancados,
  setFiltrosAvancados,
  setFiltrosAplicadosImpressao,
  setValidationError,
}) => {
  const sessionService = new SessionService();
  const { usuario, empresa } = sessionService.getEmpresaAndUsuario();
  const [fornecedorList, setFornecedorList] = useState([]);
  const [meioPagamentoList, setMeioPagamentoList] = useState([]);
  const [contasList, setContasList] = useState([]);
  const [planoFinanceiroList, setPlanoFinanceiroList] = useState([]);
  const [centroCustoList, setCentroCustoList] = useState([]);
  const [listaStatusFiltrado, setListaStatusFiltrado] = useState(listaStatus);
  const { onChangeAutocomplete, onChangeDate, onChangeInput } =
    useFiltrosRelatoriosHandlers(
      setFiltrosAvancados,
      setFiltrosAplicadosImpressao,
    );

  useEffect(() => {
    setFiltrosAvancados({
      ...filtrosAvancados,
      tipo: "TITULO_NORMAL",
      grupoId: 0,
    });
  }, []);

  function resetarFiltrosDeStatus() {
    setFiltrosAvancados((prevState) => {
      const prevStateTemp = { ...prevState };
      delete prevStateTemp.status;
      return prevStateTemp;
    });
    setFiltrosAplicadosImpressao((prevState) => {
      const prevStateTemp = { ...prevState };
      delete prevStateTemp["STATUS"];
      return prevStateTemp;
    });
    setListaStatusFiltrado(listaStatus);
  }

  const isStatusParcial = (status) =>
    status.value !== "BAIXADO_PARCIAL" &&
    (filtrosAvancados.status?.length || 0) <= 1 &&
    (status.value === "ABERTO" || status.value === "BAIXADO");

  const onChangeMultipleStatus = (valores = []) => {
    if (!valores?.length) {
      resetarFiltrosDeStatus();
      return;
    }
    const statusList = valores.map(({ value }) => value);
    const addValorParcial = valores.every((status) => isStatusParcial(status));
    const updatedValores = addValorParcial
      ? [...statusList, "BAIXADO_PARCIAL"]
      : statusList;
    updateFilters(updatedValores);
    updateListaStatus(updatedValores);
  };

  const formatarString = (texto) => {
    const split = texto.split("_");
    const formatStatus = split.map(
      (p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase(),
    );
    return formatStatus.join(" ");
  };

  const updateFilters = (valores) => {
    setFiltrosAvancados((prevState) => ({
      ...prevState,
      status: valores,
    }));
    setFiltrosAplicadosImpressao((prevState) => ({
      ...prevState,
      Status: valores.map(formatarString),
    }));
  };

  const updateListaStatus = (valores) => {
    const listaTemp = listaStatus.filter(
      (status) => !valores.some((value) => value === status.value),
    );
    setListaStatusFiltrado(listaTemp);
  };

  useEffect(() => {
    buscarFornecedores();
    buscarMeiosPagamento();
    buscarContas();
    buscarPlanosFinanceiro();
    buscarCentrosCusto();
    setEstadoInicial();
  }, []);

  function setEstadoInicial() {
    setFiltrosAvancados((prevState) => ({
      ...prevState,
      empresasIds: [empresa.id],
    }));

    if (usuario?.acessosEmpresas?.length) {
      setFiltrosAplicadosImpressao((prevState) => ({
        ...prevState,
        ["Empresas-Associadas"]: empresa.razaoSocial,
      }));
    }
  }

  const buscarFornecedores = () => {
    const filtros = {
      nonPaginated: true,
      tiposPessoaId: 2,
    };
    pessoaService.getAll(filtros).then((result) => {
      if (!result.isAxiosError) {
        setFornecedorList(result.data);
      } else {
        responseErros(result);
      }
    });
  };

  const buscarMeiosPagamento = () => {
    meioPagamentoService.getAll({ ativado: true }).then((result) => {
      if (!result.isAxiosError) {
        setMeioPagamentoList(result.data.rows);
      } else {
        responseErros(result);
      }
    });
  };

  const buscarContas = () => {
    const filtro = {
      nonPaginated: true,
    };
    bancosCaixasService.getAll(filtro).then((result) => {
      if (!result.isAxiosError) {
        setContasList(result.data);
      } else {
        responseErros(result);
      }
    });
  };

  const buscarPlanosFinanceiro = () => {
    const filtro = {
      nonPaginated: true,
    };
    planoFinanceiroService.getAll(filtro).then((result) => {
      if (!result.isAxiosError) {
        setPlanoFinanceiroList(result.data);
      } else {
        responseErros(result);
      }
    });
  };

  const buscarCentrosCusto = () => {
    const filtro = {
      nonPaginated: true,
    };
    centroCustoService.getAll(filtro).then((result) => {
      if (!result.isAxiosError) {
        setCentroCustoList(result.data);
      } else {
        responseErros(result);
      }
    });
  };

  const getMultipleAutocompleteValues = (valores, lista) => {
    if (valores) {
      return valores.map((valor) => lista.find((item) => item.id === valor));
    }
    return [];
  };

  const getSingularFilterKey = (name) => {
    switch (name) {
      case "planosFinanceirosIds":
        return "planoFinanceiroId";
      case "centrosCustoIds":
        return "centroCustoId";
      default:
        return undefined;
    }
  };

  const resetarFiltros = (name, nameImpressao) => {
    setFiltrosAvancados((prevState) => {
      const prevStateTemp = { ...prevState };
      delete prevStateTemp[name];
      const singularKey = getSingularFilterKey(name);
      if (singularKey) {
        delete prevStateTemp[singularKey];
      }
      return prevStateTemp;
    });

    setFiltrosAplicadosImpressao((prevState) => {
      const prevStateTemp = { ...prevState };
      delete prevStateTemp[nameImpressao];
      return prevStateTemp;
    });
  };

  const onChangeMultiple = (
    valores,
    name,
    nameImpressao,
    formatItem = (item) => item?.descricao || item?.nome || item?.label || "",
  ) => {
    if (!valores?.length) {
      resetarFiltros(name, nameImpressao);
      return;
    }
    setFiltrosAvancados((prevState) => {
      const nextState = {
        ...prevState,
        [name]: valores.map(({ id }) => id),
      };
      const singularKey = getSingularFilterKey(name);
      if (singularKey) {
        delete nextState[singularKey];
      }
      return nextState;
    });

    setFiltrosAplicadosImpressao((prevState) => ({
      ...prevState,
      [nameImpressao]: valores.map(formatItem).filter(Boolean).join(" | "),
    }));
  };

  function onChangeMultipleEmpresas(empresas) {
    if (empresas?.length === 0) {
      return;
    }
    setFiltrosAvancados((prevState) => ({
      ...prevState,
      empresasIds: empresas.map(({ id }) => id),
    }));
    setFiltrosAplicadosImpressao((prevState) => ({
      ...prevState,
      "Empresas-Associadas": empresas
        .map(({ razaoSocial }) => razaoSocial)
        .join(" | "),
    }));
  }

  return (
    <>
      {usuario?.acessosEmpresas?.length > 0 && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                id="empresasIds"
                name="empresasIds"
                options={usuario.acessosEmpresas}
                noOptionsText="Sem opções"
                autoHighlight
                disablePortal
                filterSelectedOptions
                getOptionLabel={(option) => option?.razaoSocial || ""}
                onChange={(_, newValue) => onChangeMultipleEmpresas(newValue)}
                value={value.multiple(
                  usuario.acessosEmpresas || [],
                  filtrosAvancados["empresasIds"]
                    ? filtrosAvancados["empresasIds"]
                    : "",
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    required
                    label="Empresas"
                    margin="normal"
                  />
                )}
              />
            </Grid>
          </Grid>
          <Divider sx={{ mt: 2, mb: 1, border: "1px solid grey" }} />
        </>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl variant="outlined" margin="normal" fullWidth>
            <Autocomplete
              id="grupoId"
              name="grupoId"
              options={listaAgrupadoresContasPagar}
              noOptionsText="Sem opções"
              autoHighlight
              disablePortal
              disableClearable
              getOptionLabel={(option) => option.label ?? ""}
              onChange={(_, newValue) =>
                onChangeAutocomplete("grupoId", newValue, "Agrupado-Por")
              }
              value={value.autoComplete(
                listaAgrupadoresContasPagar,
                filtrosAvancados?.grupoId,
              )}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" label="Agrupar Por" />
              )}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Divider sx={{ mt: 2, mb: 1, border: "1px solid grey" }} />
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl variant="outlined" margin="normal" fullWidth>
            <Autocomplete
              id="intPessoaId"
              name="Recebedor"
              options={fornecedorList}
              autoHighlight
              disablePortal
              getOptionLabel={(option) => option.nomeRazaoSocial}
              getOptionSelected={(option, value) =>
                option?.nomeRazaoSocial === value?.nomeRazaoSocial
              }
              noOptionsText="Sem opções"
              renderInput={(params) => (
                <TextField {...params} label="Recebedor" variant="outlined" />
              )}
              onChange={(e, value) =>
                onChangeAutocomplete("intPessoaId", value, "Recebedor")
              }
              value={value.autoComplete(
                fornecedorList,
                filtrosAvancados?.intPessoaId,
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <Select
            label="Tipo"
            id="tipo"
            name="tipo"
            variant="outlined"
            margin="normal"
            value={value.text(filtrosAvancados?.tipo)}
            onChange={(event) => onChangeInput(event, "Tipo")}
            SelectProps={{
              MenuProps: {
                disablePortal: true,
                PaperProps: {
                  sx: {
                    zIndex: 1600,
                  },
                },
              },
            }}
          >
            {listaTiposContasPagar.map((tipo, index) => (
              <MenuItem key={index} value={tipo.value}>
                {tipo.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl variant="outlined" margin="normal" fullWidth>
            <Autocomplete
              id="meioPagamentoId"
              name="Meio-de-Pagamento"
              options={meioPagamentoList}
              autoHighlight
              disablePortal
              getOptionLabel={(option) =>
                option.id && option.descricao
                  ? `${option.id} - ${option.descricao}`
                  : ""
              }
              getOptionSelected={(option, value) =>
                option?.descricao === value?.descricao
              }
              noOptionsText="Sem opções"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Meios de pagamento"
                  variant="outlined"
                />
              )}
              onChange={(e, value) =>
                onChangeAutocomplete(
                  "meioPagamentoId",
                  value,
                  "Meio-de-Pagamento",
                )
              }
              value={value.autoComplete(
                meioPagamentoList,
                filtrosAvancados?.meioPagamentoId,
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl variant="outlined" margin="normal" fullWidth>
            <Autocomplete
              id="finBancoCaixaId"
              name="Conta"
              options={contasList}
              autoHighlight
              disablePortal
              getOptionLabel={(option) => option.nome ?? ""}
              getOptionSelected={(option, value) =>
                option?.nome === value?.nome
              }
              noOptionsText="Sem opções"
              renderInput={(params) => (
                <TextField {...params} label="Conta" variant="outlined" />
              )}
              onChange={(e, value) =>
                onChangeAutocomplete("finBancoCaixaId", value, "Conta")
              }
              value={value.autoComplete(
                contasList,
                filtrosAvancados?.finBancoCaixaId,
              )}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <FormControl variant="outlined" margin="normal" fullWidth>
          <Autocomplete
            multiple
            id="planosFinanceiroIds"
            name="planosFinanceiroIds"
            options={planoFinanceiroList}
            autoHighlight
            disablePortal
            filterSelectedOptions
            getOptionLabel={(option) =>
              option.id && option.descricao
                ? `${option.id} - ${option.descricao}`
                : ""
            }
            noOptionsText="Sem opções"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Plano Financeiro"
                variant="outlined"
              />
            )}
            onChange={(_, newValue) =>
              onChangeMultiple(
                newValue,
                "planosFinanceiroIds",
                "Planos Financeiros",
                (option) =>
                  option?.descricao || option?.nome || String(option?.id),
              )
            }
            value={getMultipleAutocompleteValues(
              filtrosAvancados?.planosFinanceiroIds,
              planoFinanceiroList,
            )}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl variant="outlined" margin="normal" fullWidth>
          <Autocomplete
            multiple
            id="centrosCustoIds"
            name="centrosCustoIds"
            options={centroCustoList}
            autoHighlight
            disablePortal
            filterSelectedOptions
            getOptionLabel={(option) =>
              option.codigo && option.descricao
                ? `${option.codigo} - ${option.descricao}`
                : ""
            }
            noOptionsText="Sem opções"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Centro de Custo"
                variant="outlined"
              />
            )}
            onChange={(_, newValue) =>
              onChangeMultiple(
                newValue,
                "centrosCustoIds",
                "Centros de Custo",
                (option) =>
                  option.codigo && option.descricao
                    ? `${option.codigo} - ${option.descricao}`
                    : option?.descricao || option?.nome || "",
              )
            }
            value={getMultipleAutocompleteValues(
              filtrosAvancados?.centrosCustoIds,
              centroCustoList,
            )}
          />
        </FormControl>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <DatePicker
            id="dataLancamentoStart"
            name="Data-Lançamento-De"
            label="Data Lançamento De"
            margin
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataLancamentoStart)}
            onChange={(date) =>
              onChangeDate("dataLancamentoStart", date, "Data-Lançamento-De")
            }
            slotProps={{
              popper: { sx: { zIndex: 2000 } },
            }}
            setError={setValidationError}
          />
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            id="dataLancamentoEnd"
            name="Data-Lançamento-Até"
            label="Data Lançamento Até"
            margin
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataLancamentoEnd)}
            onChange={(date) =>
              onChangeDate("dataLancamentoEnd", date, "Data-Lançamento-Até")
            }
            slotProps={{
              popper: { sx: { zIndex: 2000 } },
            }}
            setError={setValidationError}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <DatePicker
            id="dataVencimentoStart"
            name="Data-Vencimento-De"
            label="Data Vencimento De"
            margin
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataVencimentoStart)}
            onChange={(date) =>
              onChangeDate("dataVencimentoStart", date, "Data-Vencimento-De")
            }
            slotProps={{
              popper: { sx: { zIndex: 2000 } },
            }}
            setError={setValidationError}
          />
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            id="dataVencimentoEnd"
            name="Data-Vencimento-Até"
            label="Data Vencimento Até"
            margin
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataVencimentoEnd)}
            onChange={(date) =>
              onChangeDate("dataVencimentoEnd", date, "Data-Vencimento-Até")
            }
            slotProps={{
              popper: { sx: { zIndex: 2000 } },
            }}
            setError={setValidationError}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <DatePicker
            id="dataPagamentoStart"
            name="Data-Pagamento-De"
            label="Data Pagamento De"
            margin
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataPagamentoStart)}
            onChange={(date) =>
              onChangeDate("dataPagamentoStart", date, "Data-Pagamento-De")
            }
            slotProps={{
              popper: { sx: { zIndex: 2000 } },
            }}
            setError={setValidationError}
          />
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            id="dataPagamentoEnd"
            name="Data-Pagamento-Até"
            label="Data Pagamento Até"
            margin
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataPagamentoEnd)}
            onChange={(date) =>
              onChangeDate("dataPagamentoEnd", date, "Data-Pagamento-Até")
            }
            slotProps={{
              popper: { sx: { zIndex: 2000 } },
            }}
            setError={setValidationError}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <DatePicker
            id="dataCompetenciaStart"
            name="Data-Competencia-De"
            label="Data Competencia De"
            margin
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataCompetenciaStart)}
            onChange={(date) =>
              onChangeDate("dataCompetenciaStart", date, "Data-Competencia-De")
            }
            slotProps={{
              popper: { sx: { zIndex: 2000 } },
            }}
            setError={setValidationError}
          />
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            id="dataCompetenciaEnd"
            name="Data-Competencia-Até"
            label="Data Competencia Até"
            margin
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataCompetenciaEnd)}
            onChange={(date) =>
              onChangeDate("dataCompetenciaEnd", date, "Data-Competencia-Até")
            }
            slotProps={{
              popper: { sx: { zIndex: 2000 } },
            }}
            setError={setValidationError}
          />
        </Grid>
      </Grid>
      <Divider sx={{ mt: 2, mb: 1, border: "1px solid grey" }} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Select
            id="ordenarPor"
            name="ordenarPor"
            margin="normal"
            value={value.text(filtrosAvancados?.ordenarPor)}
            label="Ordenar Por"
            onChange={(event) => onChangeInput(event, "ordenarPor")}
            fullWidth
            SelectProps={{
              MenuProps: {
                disablePortal: true,
                PaperProps: {
                  sx: {
                    zIndex: 1600,
                  },
                },
              },
            }}
          >
            <MenuItem value={"DATA_VENCIMENTO"}>Data Vencimento</MenuItem>
            <MenuItem value={"DATA_LANCAMENTO"}>Data Lançamento</MenuItem>
            <MenuItem value={"DATA_PAGAMENTO"}>Data Pagamento</MenuItem>
          </Select>
        </Grid>
      </Grid>
      <Divider sx={{ mt: 2, mb: 1, border: "1px solid grey" }} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl variant="outlined" margin="normal" fullWidth>
            <Autocomplete
              multiple
              id="status"
              name="status"
              options={listaStatusFiltrado}
              noOptionsText="Sem opções"
              autoHighlight
              disablePortal
              getOptionLabel={(option) => {
                return (
                  <div>
                    <i
                      className="ph-fill ph-circle"
                      style={{ color: `${option.cor}`, marginRight: "10px" }}
                    ></i>
                    {option.label}
                  </div>
                );
              }}
              onChange={(_, newValue) => onChangeMultipleStatus(newValue)}
              value={
                filtrosAvancados?.status
                  ? listaStatus.filter((status) =>
                      filtrosAvancados.status.some(
                        (value) => value === status.value,
                      ),
                    )
                  : []
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  required
                  label="Status"
                />
              )}
            />
          </FormControl>
        </Grid>
      </Grid>
    </>
  );
};

export default FormFiltroAvancado;
